import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, useToast, v } from '@kss/ui';
import { Save, ArrowLeft } from 'lucide-react';
import type { Attribute, AttributeCreate } from '@kss/api';
import { attributeService } from '@kss/api';

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function NewAttribute() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const isEdit = !!id && id !== 'new';
  const [form, setForm] = useState({ name: '', description: '' });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  /* Was presence-only via `!form.name.trim()` on the button — markup and
     unbounded strings both saved. */
  const errs: Record<string, string | null> = {
    name: v.runRules(form.name, [v.required('Name'), v.maxLength(80, 'Name'), ...v.safeText('Name')]),
    description: v.runRules(form.description, [
      v.maxLength(500, 'Description'),
      ...v.safeText('Description'),
    ]),
  };
  const fErr = (k: string) => (touched[k] ? errs[k] ?? undefined : undefined);
  const markF = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const formValid = !Object.values(errs).some(Boolean);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let c = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try { const r = await attributeService.get(id); if (!c) setForm({ name: r.name ?? '', description: r.description ?? '' }); }
        catch (e) { const msg = e instanceof Error ? e.message : 'Not found'; setErr(msg); toastRef.current.error('Load failed', msg); }
        finally { if (!c) setLoading(false); }
      }
    }
    run(); return () => { c = true; };
  }, [id, isEdit]);

  const save = async () => {
    setTouched({ name: true, description: true });
    const problem = Object.values(errs).find(Boolean);
    if (problem) { toast.error('Fix errors', problem); return; }
    setSaving(true);
    try {
      if (isEdit && id) { await attributeService.update([{ attributeId: id, name: form.name, description: form.description || undefined } as Attribute]); toast.success('Saved', `${form.name} updated`); }
      else { await attributeService.create([{ name: form.name, description: form.description || undefined } as AttributeCreate]); toast.success('Saved', `${form.name} added`); }
      navigate('/panel/attributes');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/attributes')}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-base font-semibold text-content-primary">{isEdit ? 'Edit attribute' : 'New attribute'}</h2>
        </div>
        {loading ? <div className="text-sm text-content-muted">Loading…</div> : (
          <div className="grid gap-3">
            <label className="flex flex-col gap-1"><span className="text-xs font-medium text-content-secondary">Name <span className="text-danger">*</span></span>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onBlur={markF("name")} placeholder="Colour" className={inputCls + (fErr("name") ? " border-danger" : "")} />
              {fErr("name") && <span className="text-xs text-danger">{fErr("name")}</span>}</label>
            <label className="flex flex-col gap-1"><span className="text-xs font-medium text-content-secondary">Description</span>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} onBlur={markF("description")} rows={3} className={inputCls + " h-auto py-2" + (fErr("description") ? " border-danger" : "")} placeholder="Optional description..." />
              {fErr("description") && <span className="text-xs text-danger">{fErr("description")}</span>}</label>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate('/panel/attributes')}>Cancel</Button>
          <Button onClick={save} disabled={saving || !formValid}><Save className="h-4 w-4" />{saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  );
}
